import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import classNames from 'classnames';
import { useParams } from "react-router";

import { useTranslate } from "../../../../../i18n/useTranslationModules";
import { PageModule, SurveyAnswerBody, Survey, SurveyQuestion } from "../../../../../types/working-model";
import { updateTranslateKey } from "../../../../../utils/utils";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import Textarea from "../../../../general-ui/textarea/textarea";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { SubmitSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { getStorageObject, setStorageObject } from "../../../../../utils/local-storage";
import { getPollAnswerLocalStorageKey, getPollAnswerLocalStorageKeyBackwardsCompatible } from "./survey";
import useTranslationRoute from "../../../hooks/use-translation-route";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { OptionalComponent } from "../../../../../utils/optional-component";

interface ITextSurveyQuestionProps {
	question: SurveyQuestion;
	module: PageModule;
	template: string;
	sessionUuid?: string;
	survey: Survey;
	setCurrentQuestion: Dispatch<SetStateAction<number>>;
	currentQuestion: number;
	maxQuestions: number;
	viewAll: boolean;
}


const TextSurveyQuestion: React.FC<ITextSurveyQuestionProps> = ({
	question,
	module,
	template,
	sessionUuid,
	survey,
	setCurrentQuestion,
	currentQuestion,
	maxQuestions,
	viewAll
}) => {
	const { survey: surveyId } = survey;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;

	const { language }: ParamsProps = useParams();

	const [isAnswered, setIsAnswered] = useState(false);
	const [value, setValue] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const event = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);
	const sessions = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.sessions);
	const registrationId = useTypedSelector((state) => state.LiveEventReducer.registrationId);
	const registration_on = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.registration_on);
	const workingSession = useTypedSelector((state) => state.CreateEventReducer.workingSession);
	const previewMode = useTypedSelector((state) => state.CreateEventReducer.previewMode);
	const token = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const channel = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.channel);
	const leaderboardEnabled = !!useTypedSelector(state => state.LiveEventReducer.leaderboardIsOn);

	const session = useMemo(() => {
		if (previewMode === 'session') return workingSession;

		return sessions?.find(session => session.uuid === sessionUuid);
	}, [previewMode, sessions, sessionUuid, workingSession]);
	// const sessionId = session?.session;
	const namespace = useTranslationRoute();
	const { t } = useTranslate([namespace, "session"]);
	const isSubmitDisabled = isAnswered || !value || isSubmitting;

	useEffect(() => {
		if (!event) return;

		// if the user refreshes we want to keep the answers they submited shown
		const lsKey = getPollAnswerLocalStorageKey(question, surveyId, event);
		const lsKeyBackwardsCompatible = getPollAnswerLocalStorageKeyBackwardsCompatible(question, surveyId);
		const answer: SurveyAnswerBody | null = getStorageObject(lsKey) ?? getStorageObject(lsKeyBackwardsCompatible);

		if (answer?.event === event && answer.text_answer) {
			// update the store with the selected options
			const text = answer.text_answer[language] as string | undefined || answer.text_answer.base;
			setValue(text);
			setIsAnswered(true);
		}
	}, [event, language, question, surveyId]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (question.required && !value.trim().length) return;
		if (!module.id || !event) return;

		const translateKeyValues = updateTranslateKey({
			translateString: { base: '', changed: '' },
			input: value,
			baseLanguage: language, //because this will be end user submited, we want whichever language they are on to be the base
			language
		});

		const body: SurveyAnswerBody = {
			session: session?.session,
			survey_question: question.survey_question,
			module: module.id,
			event: event,
			text_answer: translateKeyValues,
			radio_answer: null,
			checkbox_answer: null,
			session_uuid: session?.uuid,
			language: language,
			channel,
			leaderboard_enabled: leaderboardEnabled
		};
		if (registration_on && registrationId) {
			body.registration_id = registrationId;
		}

		try {
			//send to microservices
			setIsSubmitting(true);
			await SubmitSurveyAnswer(body, token);

			//set in local storage in case they refresh
			const lsKey = getPollAnswerLocalStorageKey(question, surveyId, event);
			setStorageObject(lsKey, body, 48);
			setIsAnswered(true);
		} catch (err) {
			console.error(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setValue(e.target.value);
	};

	const showNext = () => {
		return (
			<button
				disabled={currentQuestion + 1 === maxQuestions}
				onClick={() => setCurrentQuestion(currentQuestion + 1)}>
				{t("session:Next")}
			</button>
		);
	};

	const showSubmit = () => {
		return (
			<button
				className={classNames('primary next', primaryButtonOverrides)} type="submit"
				onClick={handleSubmit}
				disabled={isSubmitDisabled}>
				{isSubmitting ? (
					<WaitingIndicator />
				) :
					t(`${namespace}:callToAction`, (content?.callToAction?.button_name?.[language] || t("session:Submit")))
				}
			</button>
		);
	};

	const displayButtonRow = () => {
		return (
			<div className='survey-pagination'>
				{!viewAll && <button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("session:Previous")}</button>}
				<div>
					<OptionalComponent display={!viewAll}>
						<span>{currentQuestion + 1}/{maxQuestions}</span>
					</OptionalComponent>
				</div>
				{viewAll || !isAnswered || (value && !isSubmitDisabled) || isSubmitting ? (
					<>
						{showSubmit()}
					</>
				) : (
					<>
						{showNext()}
					</>
				)}
			</div >
		);
	};

	return (
		<form onSubmit={handleSubmit} className="question-container">
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{t(`${namespace}:survey.${surveyId}.questions.${question?.survey_question}`, (question?.title?.[language] as string ?? question?.title?.base))}
			</TypographyItem>

			<div className="survey-text-actions-container">
				<Textarea placeholder={t("Enter your response here")} defaultValue={value ?? ''} onChange={handleValueChange} disabled={isAnswered || (previewMode === 'session')} shouldUpdateValue={true} />
				<div className={classNames('submit-action')}>
					{displayButtonRow()}
				</div >
			</div >
		</form >
	);
};

export default TextSurveyQuestion;
